import { useRequirementImportSummary } from '@/features/requirement-imports/hooks/useRequirementImports';
import { useAccess } from '@/features/access/access';
import { useState } from 'react';
import { useIsMutating } from '@tanstack/react-query';
import { SpinnerIcon } from '@/components/icons';
import { queryKeys } from '@/lib/query/query-keys';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router';
import { ROUTES } from '@/app/router/routes';
import { useAuth } from '@/features/auth/context/auth-context';
import { useCvImportSummary } from '../hooks/useCvImports';
import { CvImportToast } from './CvImportToast';
import '../cv-imports.css';

type Cursor = { date: string; ids: string[] };
const readCursor = (key: string): Cursor => {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return { date: '', ids: [] };
    const value: unknown = JSON.parse(raw);
    if (
      value &&
      typeof value === 'object' &&
      'date' in value &&
      typeof value.date === 'string' &&
      'ids' in value &&
      Array.isArray(value.ids)
    )
      return {
        date: value.date,
        ids: value.ids.filter((id): id is string => typeof id === 'string'),
      };
  } catch {
    /* Storage can be unavailable. */
  }
  return { date: '', ids: [] };
};

/** Dismissal is local; unread results remain available in the persistent journal. */
export const CvImportNotifications = () => {
  const summary = useCvImportSummary();
  const needsSummary = useRequirementImportSummary();
  const needsActive =
    (needsSummary.data?.counts.PENDING ?? 0) + (needsSummary.data?.counts.PROCESSING ?? 0);
  const needsAwaiting = needsSummary.data?.counts.AWAITING_ASSIGNMENT ?? 0;
  const needsUploading =
    useIsMutating({ mutationKey: [...queryKeys.requirementImports.all, 'upload'] }) > 0;
  const access = useAccess();
  const canCvs = access.can('candidates', 'read');
  const uploading = useIsMutating({ mutationKey: [...queryKeys.cvImports.all, 'upload'] }) > 0;
  const { user } = useAuth();
  const location = useLocation();
  const storageKey = 'cv-import-notice-v2:' + user?.id;
  const [cursor, setCursor] = useState(() => readCursor(storageKey));
  const data = summary.data;
  const active = (data?.counts.PENDING ?? 0) + (data?.counts.PROCESSING ?? 0);
  const unread = data?.unread ?? 0;
  const busy =
    uploading ||
    needsUploading ||
    active > 0 ||
    needsActive > 0 ||
    summary.isFetching ||
    needsSummary.isFetching;
  const items = (data?.latest ?? []).filter(
    (item) =>
      item.processedAt &&
      (item.processedAt > cursor.date ||
        (item.processedAt === cursor.date && !cursor.ids.includes(item.id))),
  );
  // Include IDs at equal timestamps: multiple workers can finish in the same millisecond.
  const date = items.reduce(
    (latest, item) => (item.processedAt && item.processedAt > latest ? item.processedAt : latest),
    cursor.date,
  );
  const ids = [
    ...new Set([
      ...(date === cursor.date ? cursor.ids : []),
      ...items.filter((item) => item.processedAt === date).map((item) => item.id),
    ]),
  ];
  const dismiss = () => {
    const next: Cursor = { date, ids };
    setCursor(next);
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(next));
    } catch {
      /* Private browsing. */
    }
  };
  const label = needsUploading
    ? 'Envoi des besoins en cours'
    : needsActive > 0
      ? needsActive + ' import(s) de besoins en cours'
      : needsAwaiting > 0
        ? needsAwaiting + ' import(s) de besoins à affecter'
        : uploading
          ? 'Envoi des CV en cours'
          : canCvs && summary.isPending
            ? 'Chargement du suivi des imports'
            : summary.isError
              ? 'Suivi des imports indisponible — ouvrir l’historique'
              : unread > 0
                ? unread +
                  ' résultat' +
                  (unread > 1 ? 's' : '') +
                  ' d’import de CV non lu' +
                  (unread > 1 ? 's' : '')
                : active > 0
                  ? active + ' import' + (active > 1 ? 's' : '') + ' de CV en cours'
                  : 'Suivi des imports';
  return (
    <>
      <Link
        className="cv-import-notification-link"
        to={
          ROUTES.candidateImport +
          (needsActive > 0 || needsAwaiting > 0 || needsUploading ? '?type=requirements' : '')
        }
        title={label}
        aria-label={label}
        aria-busy={busy}
        aria-current={location.pathname === ROUTES.candidateImport ? 'page' : undefined}
        onClick={dismiss}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M12 3v10m-4-4 4 4 4-4M5 10l-2 5v5a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1v-5l-2-5" />
          <path d="M3 15h5l2 3h4l2-3h5" />
        </svg>
        {unread + needsAwaiting > 0 ? (
          <span className="cv-import-notification-count" aria-hidden>
            {unread + needsAwaiting > 99 ? '99+' : unread + needsAwaiting}
          </span>
        ) : null}
        {busy && (
          <span
            className="cv-import-notification-loading"
            role="status"
            aria-label={
              needsUploading
                ? 'Envoi des besoins en cours'
                : needsActive > 0
                  ? 'Traitement des besoins en cours'
                  : uploading
                    ? 'Envoi des CV en cours'
                    : active > 0
                      ? 'Traitement des CV en cours'
                      : 'Actualisation des imports'
            }
          >
            <SpinnerIcon aria-hidden className="animate-spin motion-reduce:animate-none" />
          </span>
        )}
      </Link>
      {items.length > 0 &&
        ![ROUTES.candidateImport, '/candidats/imports'].includes(location.pathname) &&
        createPortal(
          <CvImportToast
            key={items
              .map((item) => item.id)
              .sort()
              .join(',')}
            items={items}
            unread={unread}
            onDismiss={dismiss}
          />,
          document.body,
        )}
    </>
  );
};
